import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageSquareHeart } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function FeedbackPage() {
  const { t } = useI18n();
  const [content, setContent] = useState({});
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    category: "saran",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => {
    api.get("/content/feedback").then((res) => setContent(res.data)).catch(() => {});
  }, []);

  const c = (key, fallback = "") => content[key] || fallback;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error(t("feedback.toast_required", "Mohon isi nama dan pesan Anda"));
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error(t("feedback.toast_turnstile", "Mohon selesaikan verifikasi anti-spam"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { ...form, turnstile_token: turnstileToken });
      toast.success(t("feedback.toast_success", "Terima kasih! Pesan Anda telah terkirim."));
      setForm({ name: "", email: "", whatsapp: "", category: "saran", message: "" });
      setTurnstileToken("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#FDFBF7] text-[#1C1917] min-h-screen" data-testid="feedback-page">
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[280px] md:w-[440px] h-[280px] md:h-[440px] rounded-full bg-[#F39237]/15 blur-3xl pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 overline text-[#F39237] mb-4">
              <MessageSquareHeart size={16} />
              {c("hero_overline", t("feedback.hero_overline", "Kritik & Saran"))}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] text-[#0A0A0A] mb-5">
              {c("hero_title", t("feedback.hero_title", "Suara Anda membentuk Aurora."))}
            </h1>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed">
              {c("hero_subtitle", t("feedback.hero_subtitle", "Kami sangat menghargai setiap masukan. Sampaikan pendapat, keluhan, atau ide untuk membantu kami tumbuh lebih baik."))}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form
            onSubmit={submit}
            data-testid="feedback-form"
            className="bg-white border border-stone-200 p-6 md:p-12 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field
                label={t("feedback.name", "Nama")}
                testId="fb-name"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder={t("feedback.name_ph", "Nama Anda")}
                required
              />
              <div>
                <label className="block overline text-stone-500 mb-2">{t("feedback.category", "Kategori")}</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="fb-category" className="h-12 bg-transparent border-stone-300 rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saran">{t("feedback.cat_saran", "Saran")}</SelectItem>
                    <SelectItem value="kritik">{t("feedback.cat_kritik", "Kritik")}</SelectItem>
                    <SelectItem value="pertanyaan">{t("feedback.cat_pertanyaan", "Pertanyaan")}</SelectItem>
                    <SelectItem value="lainnya">{t("feedback.cat_lainnya", "Lainnya")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field
                label={t("feedback.email", "Email (opsional)")}
                testId="fb-email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                placeholder="nama@email.com"
              />
              <Field
                label={t("feedback.whatsapp", "WhatsApp (opsional)")}
                testId="fb-whatsapp"
                value={form.whatsapp}
                onChange={(v) => setForm({ ...form, whatsapp: v })}
                placeholder="08xxxxxxxxxx"
              />
            </div>
            <div>
              <label className="block overline text-stone-500 mb-2">{t("feedback.message", "Pesan Anda")}</label>
              <textarea
                data-testid="fb-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("feedback.message_ph", "Ceritakan masukan atau pertanyaan Anda…")}
                className="w-full px-4 py-3 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none resize-none"
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div data-testid="fb-turnstile">
                <Turnstile
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken("")}
                  options={{ theme: "light", size: "flexible" }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="fb-submit"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] hover:-translate-y-1 transition-all shadow-md hover:shadow-xl disabled:opacity-60 disabled:hover:transform-none"
            >
              {submitting ? t("feedback.submitting", "Mengirim…") : t("feedback.submit", "Kirim Pesan")}
              <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, placeholder, required, testId }) {
  return (
    <div>
      <label className="block overline text-stone-500 mb-2">{label}</label>
      <input
        type="text"
        data-testid={testId}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-transparent border border-stone-300 focus:border-[#F39237] focus:ring-2 focus:ring-[#F39237]/20 outline-none"
      />
    </div>
  );
}
